import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Dica from '@/components/feature/Dica';
import TelaTitulo from '@/pages/gerenciar/painel/TelaTitulo';
import AbaPortaria from '@/pages/gerenciar/painel/AbaPortaria';
import EquipeDoDia from '@/pages/gerenciar/painel/equipe-dia/EquipeDoDia';
import {
  inicioDoDiaBrasiliaISO,
  normalizarQuem,
  normalizarVendas,
} from '@/pages/gerenciar/painel/equipe-dia/utils';
import ConteudoLoja from '@/pages/gerenciar/loja/ConteudoLoja';
import HubDiaEvento from './HubDiaEvento';
import type { TelaDia } from './HubDiaEvento';
import DeslizarEntrada from '@/components/movimento/DeslizarEntrada';

interface Props {
  eventoId: number;
  eventoNome: string;
  mostrarLoja: boolean;
  vendeIngressos: boolean;
  /** Entrada franca (Quermesse): não tem ingresso para ler na portaria. */
  entradaFranca?: boolean;
  rotuloPonto?: string;
  rotuloPontos?: string;
}

const TELAS: TelaDia[] = [
  'vendas',
  'barracas',
  'servindo',
  'maquininhas',
  'portaria',
  'fichas',
  'estado',
];

const TITULO: Record<TelaDia, string> = {
  vendas: 'Vendas de hoje',
  barracas: 'Barracas e produtos',
  servindo: 'Quem está servindo',
  maquininhas: 'Maquininhas e caixas',
  portaria: 'Portaria',
  fichas: 'Link e cartaz das fichas',
  estado: 'Vendas pelo site',
};

const DICA: Record<TelaDia, string> = {
  vendas: 'painel.equipe',
  barracas: 'painel.loja',
  servindo: 'painel.equipe',
  maquininhas: 'painel.loja',
  portaria: 'painel.portaria',
  fichas: 'painel.loja',
  estado: 'painel.loja',
};

// Aba "Dia do evento": um hub só, no lugar de Loja + Equipe do dia + Portaria.
export default function AbaDiaEvento({
  eventoId,
  eventoNome,
  mostrarLoja,
  vendeIngressos,
  entradaFranca = false,
  rotuloPonto,
  rotuloPontos,
}: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [vendasHoje, setVendasHoje] = useState(0);
  const [noCelular, setNoCelular] = useState(0);
  const [convites, setConvites] = useState(0);
  const [portaria, setPortaria] = useState<{ entraram: number; validos: number } | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const [rVendas, rQuem, rConvites, rPortaria] = await Promise.allSettled([
        supabase.rpc('equipe_vendas', {
          p_evento: eventoId,
          p_desde: inicioDoDiaBrasiliaISO(),
          p_ate: null,
        }),
        supabase.rpc('equipe_quem_e_quem', { p_evento: eventoId }),
        supabase.rpc('equipe_convites_do_evento', { p_evento: eventoId }),
        supabase
          .from('vw_portaria')
          .select('ja_entraram, ingressos_validos')
          .eq('evento_id', eventoId)
          .maybeSingle(),
      ]);
      if (!ativo) return;

      if (rVendas.status === 'fulfilled' && !rVendas.value.error) {
        const v = normalizarVendas(rVendas.value.data);
        setVendasHoje(v ? Number(v.resumo.caixa_total_centavos) || 0 : 0);
      }
      if (rQuem.status === 'fulfilled' && !rQuem.value.error) {
        const linhas = Array.isArray(rQuem.value.data)
          ? (rQuem.value.data as Record<string, unknown>[]).map(normalizarQuem)
          : [];
        setNoCelular(linhas.filter((q) => q.como === 'link' && q.situacao === 'aceito').length);
      }
      if (rConvites.status === 'fulfilled' && !rConvites.value.error) {
        const linhas = Array.isArray(rConvites.value.data)
          ? (rConvites.value.data as Record<string, unknown>[])
          : [];
        setConvites(
          linhas.filter((c) => String(c.situacao ?? '').toLowerCase() === 'aguardando').length,
        );
      }
      if (rPortaria.status === 'fulfilled' && !rPortaria.value.error && rPortaria.value.data) {
        const d = rPortaria.value.data as {
          ja_entraram: number | null;
          ingressos_validos: number | null;
        };
        setPortaria({
          entraram: Number(d.ja_entraram) || 0,
          validos: Number(d.ingressos_validos) || 0,
        });
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const telaDaUrl = searchParams.get('tela');
  const tela: TelaDia | null =
    telaDaUrl && TELAS.includes(telaDaUrl as TelaDia) ? (telaDaUrl as TelaDia) : null;

  function abrirTela(destino: TelaDia) {
    const next = new URLSearchParams(searchParams);
    next.set('aba', 'dia');
    next.set('tela', destino);
    next.delete('sub');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function voltarAoHub() {
    const next = new URLSearchParams(searchParams);
    next.delete('tela');
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (!tela) {
    return (
      <DeslizarEntrada className="space-y-4">
        <div className="flex items-center gap-2 px-1">
          <h2 className="text-[17px] font-bold text-foreground-950">Dia do evento</h2>
          <Dica id="painel.dia" eventoId={eventoId} />
        </div>

        <HubDiaEvento
          temLoja={mostrarLoja}
          vendeIngressos={vendeIngressos}
          entradaFranca={entradaFranca}
          vendasHojeCentavos={vendasHoje}
          noCelular={noCelular}
          convitesAguardando={convites}
          portaria={portaria}
          aoAbrir={abrirTela}
          aoAbrirCaixa={() => navigate('/caixa')}
          aoImprimirFichas={() => navigate(`/gerenciar/${eventoId}/loja/fichas-imprimir`)}
        />
      </DeslizarEntrada>
    );
  }

  return (
    <DeslizarEntrada>
      <TelaTitulo
        titulo={TITULO[tela]}
        aoVoltar={voltarAoHub}
        dicaId={DICA[tela]}
        eventoId={eventoId}
      />

      {tela === 'vendas' && (
        <EquipeDoDia eventoId={eventoId} eventoNome={eventoNome} visaoFixa="vendas" />
      )}
      {tela === 'servindo' && (
        <EquipeDoDia eventoId={eventoId} eventoNome={eventoNome} visaoFixa="equipe" />
      )}
      {tela === 'barracas' && (
        <ConteudoLoja
          eventoId={eventoId}
          rotuloPonto={rotuloPonto}
          rotuloPontos={rotuloPontos}
          secoes={['barracas', 'produtos']}
        />
      )}
      {tela === 'maquininhas' && (
        <ConteudoLoja eventoId={eventoId} secoes={['caixa', 'maquininhas']} />
      )}
      {tela === 'fichas' && <ConteudoLoja eventoId={eventoId} secoes={['fichas']} />}
      {tela === 'estado' && <ConteudoLoja eventoId={eventoId} secoes={['estado']} />}
      {tela === 'portaria' && <AbaPortaria eventoId={eventoId} />}
    </DeslizarEntrada>
  );
}